import React, { createContext, useContext, useReducer, useEffect } from 'react'
import { authService } from '../services/authService'
import { courseService } from '../services/courseService'
import { assessmentService } from '../services/assessmentService'
import { studentService } from '../services/studentService'
import { performanceService } from '../services/performanceService'

const AppContext = createContext()

const initialState = {
  user: null,
  profile: null,
  courses: [],
  activeCourse: null,
  archivedReports: [],
  assessmentPlan: [],
  sltWeightage: [],
  cloStatements: {},
  ploStatements: {},
  cloMapping: {},
  ploMapping: {},
  attainmentTargets: { cloTargets: {}, ploTargets: {} }, // Store attainment targets
  studentsData: [],
  sltConfiguration: { courseSLTConfig: null, teachingSLTByCLO: null, breakdownConfig: null },
  loading: false,
  error: null
}

const appReducer = (state, action) => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload }
    case 'SET_ERROR':
      return { ...state, error: action.payload }
    case 'SET_USER':
      return { ...state, user: action.payload }
    case 'SET_PROFILE':
      return { ...state, profile: action.payload }
    case 'LOGOUT':
      return { ...initialState }
    case 'SET_COURSES':
      return { ...state, courses: action.payload }
    case 'ADD_COURSE':
      return { ...state, courses: [...state.courses, action.payload] }
    case 'UPDATE_COURSE':
      return { ...state, courses: state.courses.map(course => course.id === action.payload.id ? action.payload : course) }
    case 'DELETE_COURSE':
      return { ...state, courses: state.courses.filter(c => c.id !== action.payload) }
    case 'SET_ACTIVE_COURSE':
      return { ...state, activeCourse: action.payload }
    case 'SET_ASSESSMENT_PLAN':
      return { ...state, assessmentPlan: action.payload }
    case 'SET_CLO_STATEMENTS':
      return { ...state, cloStatements: action.payload }
    case 'SET_PLO_STATEMENTS':
      return { ...state, ploStatements: action.payload }
    case 'SET_CLO_MAPPING':
      return { ...state, cloMapping: action.payload }
    case 'SET_PLO_MAPPING':
      return { ...state, ploMapping: action.payload }
    case 'SET_ATTAINMENT_TARGETS':
      console.log('🎯 Context: Setting attainment targets:', action.payload);
      // CRITICAL: Ensure proper state update with immediate effect
      const newTargets = {
        cloTargets: action.payload.cloTargets || {},
        ploTargets: action.payload.ploTargets || {}
      };
      console.log('🎯 Context: Final targets to store:', newTargets);
      return { ...state, attainmentTargets: newTargets }
    case 'SET_STUDENTS_DATA':
      console.log('Updating student data in context:', action.payload?.length, 'students');
      return { ...state, studentsData: action.payload }
    case 'REFRESH_STUDENTS_DATA':
      console.log('Force refreshing student data in context');
      return { ...state, studentsData: action.payload }
    case 'SET_SLT_CONFIGURATION':
      return { ...state, sltConfiguration: action.payload }
    case 'UPDATE_COURSE_PROGRESS':
      return {
        ...state,
        courses: state.courses.map(course =>
          course.id === action.payload.courseId ? { ...course, progress: action.payload.progress } : course
        )
      }
    case 'COMPLETE_COURSE':
      return {
        ...state,
        courses: state.courses.map(course =>
          course.id === action.payload.id ? { ...course, status: 'completed', completedAt: new Date().toISOString() } : course
        ),
        archivedReports: [...state.archivedReports, action.payload]
      }
    default:
      return state
  }
}

export const AppProvider = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState)

  // Initialize app
  useEffect(() => {
    initializeApp()
  }, [])

  const initializeApp = async () => {
    dispatch({ type: 'SET_LOADING', payload: true })
    try {
      // Check for existing session
      const { user, profile } = await authService.getCurrentUser()
      if (user) {
        dispatch({ type: 'SET_USER', payload: user })
        dispatch({ type: 'SET_PROFILE', payload: profile })
        // Load user's courses
        await loadCourses()
      }
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message })
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false })
    }
  }

  const loadCourses = async () => {
    try {
      const { data, error } = await courseService.getCourses()
      if (error) throw new Error(error)
      dispatch({ type: 'SET_COURSES', payload: data || [] })
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message })
    }
  }

  const loadCourseData = async (courseId) => {
    if (!courseId) return
    dispatch({ type: 'SET_LOADING', payload: true })
    try {
      console.log('🎯 Loading course data for:', courseId);

      // Load all course-related data in parallel
      const [
        assessmentsResult,
        cloStatementsResult,
        ploStatementsResult,
        mappingResult,
        studentsResult,
        sltConfigurationResult,
        attainmentTargetsResult
      ] = await Promise.all([
        assessmentService.getAssessments(courseId),
        assessmentService.getCLOStatements(courseId),
        assessmentService.getPLOStatements(courseId),
        assessmentService.getCLOPLOMapping(courseId),
        studentService.getStudents(courseId),
        assessmentService.getSLTConfiguration(courseId),
        assessmentService.getAttainmentTargets(courseId)
      ])

      if (assessmentsResult.data) {
        const formattedAssessments = assessmentsResult.data.map(a => ({
          id: a.id,
          type: a.assessment_type,
          name: a.assessment_name,
          marks: a.marks,
          sltCI: parseFloat(a.slt_ci),
          clo: a.clo_mapping
        }));
        console.log('Loaded assessments:', formattedAssessments);
        dispatch({ type: 'SET_ASSESSMENT_PLAN', payload: formattedAssessments })
      }

      if (cloStatementsResult.data) {
        const cloStatements = cloStatementsResult.data.reduce((acc, clo) => {
          acc[clo.clo_code] = clo.statement
          return acc
        }, {})
        console.log('Loaded CLO statements:', cloStatements);
        dispatch({ type: 'SET_CLO_STATEMENTS', payload: cloStatements })
      }

      if (ploStatementsResult.data) {
        const ploStatements = ploStatementsResult.data.reduce((acc, plo) => {
          acc[plo.plo_code] = plo.statement
          return acc
        }, {})
        console.log('Loaded PLO statements:', ploStatements);
        dispatch({ type: 'SET_PLO_STATEMENTS', payload: ploStatements })
      }

      if (mappingResult.data) {
        const mapping = mappingResult.data.reduce((acc, item) => {
          if (!acc[item.clo_code]) {
            acc[item.clo_code] = {}
          }
          acc[item.clo_code][item.plo_code] = item.is_mapped
          return acc
        }, {})
        console.log('Loaded PLO mapping:', mapping);
        dispatch({ type: 'SET_PLO_MAPPING', payload: mapping })
      }

      // CRITICAL: Load attainment targets with enhanced handling
      console.log('🎯 Loading attainment targets result:', attainmentTargetsResult);
      if (!attainmentTargetsResult.error && attainmentTargetsResult.data) {
        console.log('🎯 Setting targets in context:', attainmentTargetsResult.data);
        dispatch({
          type: 'SET_ATTAINMENT_TARGETS',
          payload: {
            cloTargets: attainmentTargetsResult.data.clo_targets || {},
            ploTargets: attainmentTargetsResult.data.plo_targets || {}
          }
        });
      } else {
        console.log('🎯 No targets found, setting default targets');
        // Create default targets based on CLO and PLO statements
        const defaultCloTargets = {};
        const defaultPloTargets = {};
        
        if (cloStatementsResult.data) {
          cloStatementsResult.data.forEach(clo => {
            defaultCloTargets[clo.clo_code] = 50;
          });
        }
        
        if (ploStatementsResult.data) {
          ploStatementsResult.data.forEach(plo => {
            defaultPloTargets[plo.plo_code] = 50;
          });
        }
        
        dispatch({
          type: 'SET_ATTAINMENT_TARGETS',
          payload: {
            cloTargets: defaultCloTargets,
            ploTargets: defaultPloTargets
          }
        });
      }

      if (studentsResult.data) {
        const students = studentsResult.data.map(student => {
          const marks = {};
          // Convert student marks from array to object format
          if (student.student_marks_clomapper && student.student_marks_clomapper.length > 0) {
            student.student_marks_clomapper.forEach(mark => {
              marks[mark.assessment_id] = mark.marks;
            });
          }
          return {
            id: student.id,
            matricId: student.matric_id,
            name: student.name,
            marks: marks
          }
        })
        console.log('Loading students data in context:', students.length, 'students');
        dispatch({ type: 'SET_STUDENTS_DATA', payload: students })
      }

      // Load SLT configuration if available
      if (sltConfigurationResult.data) {
        dispatch({
          type: 'SET_SLT_CONFIGURATION',
          payload: {
            courseSLTConfig: sltConfigurationResult.data.course_slt_config,
            teachingSLTByCLO: sltConfigurationResult.data.teaching_slt_by_clo,
            breakdownConfig: sltConfigurationResult.data.breakdown_config
          }
        })
      } else {
        // Reset SLT configuration if not available
        dispatch({
          type: 'SET_SLT_CONFIGURATION',
          payload: {
            courseSLTConfig: null,
            teachingSLTByCLO: null,
            breakdownConfig: null
          }
        })
      }
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message })
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false })
    }
  }

  // Function to refresh student data specifically
  const refreshStudentData = async (courseId) => {
    if (!courseId) return
    try {
      console.log('Refreshing student data for course:', courseId);
      const { data, error } = await studentService.getStudents(courseId)
      if (error) throw new Error(error)
      if (data) {
        const students = data.map(student => ({
          id: student.id,
          matricId: student.matric_id,
          name: student.name,
          marks: student.student_marks_clomapper.reduce((acc, mark) => {
            acc[mark.assessment_id] = mark.marks
            return acc
          }, {})
        }))
        console.log('Refreshed student data:', students.length, 'students');
        dispatch({ type: 'REFRESH_STUDENTS_DATA', payload: students })
        return students
      }
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message })
      throw error
    }
  }

  // ENHANCED Function to refresh attainment targets specifically
  const refreshAttainmentTargets = async (courseId) => {
    if (!courseId) return
    try {
      console.log('🎯 Refreshing attainment targets for course:', courseId);
      const { data, error } = await assessmentService.getAttainmentTargets(courseId)
      
      if (!error && data) {
        console.log('🎯 Refreshed targets from DB:', data);
        
        // CRITICAL: Immediately update global state
        dispatch({
          type: 'SET_ATTAINMENT_TARGETS',
          payload: {
            cloTargets: data.clo_targets || {},
            ploTargets: data.plo_targets || {}
          }
        });
        
        return data
      } else {
        console.log('🎯 No targets found during refresh, creating default targets');
        
        // Create default targets based on CLO and PLO statements
        const defaultCloTargets = {};
        const defaultPloTargets = {};
        
        // Get CLO statements to create default targets
        const { data: cloData } = await assessmentService.getCLOStatements(courseId);
        if (cloData) {
          cloData.forEach(clo => {
            defaultCloTargets[clo.clo_code] = 50;
          });
        }
        
        // Get PLO statements to create default targets
        const { data: ploData } = await assessmentService.getPLOStatements(courseId);
        if (ploData) {
          ploData.forEach(plo => {
            defaultPloTargets[plo.plo_code] = 50;
          });
        }
        
        // Save default targets to database
        await assessmentService.saveAttainmentTargets(courseId, {
          clo_targets: defaultCloTargets,
          plo_targets: defaultPloTargets
        });
        
        // Update global state with default targets
        dispatch({
          type: 'SET_ATTAINMENT_TARGETS',
          payload: {
            cloTargets: defaultCloTargets,
            ploTargets: defaultPloTargets
          }
        });
        
        return {
          clo_targets: defaultCloTargets,
          plo_targets: defaultPloTargets
        };
      }
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message })
      throw error
    }
  }

  const calculateProgress = (course) => {
    const sections = ['assessmentPlan', 'cloStatements', 'ploStatements', 'cloMapping', 'ploMapping', 'studentsData']
    const completed = sections.filter(section => {
      const data = state[section]
      return Array.isArray(data) ? data.length > 0 : Object.keys(data).length > 0
    }).length
    return Math.round((completed / sections.length) * 100)
  }

  const value = {
    state,
    dispatch,
    loadCourses,
    loadCourseData,
    refreshStudentData,
    refreshAttainmentTargets, // Enhanced function
    calculateProgress,
    // Service methods
    authService,
    courseService,
    assessmentService,
    studentService,
    performanceService
  }

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  )
}

export const useApp = () => {
  const context = useContext(AppContext)
  if (!context) {
    throw new Error('useApp must be used within an AppProvider')
  }
  return context
}